import { useRef } from "react";

/**
 * Long-press + double-tap detector for Surah Mode ayahs.
 *
 * Behavior:
 *   • short tap  → invokes `onTap(ayah)` (used to complete a range).
 *   • long-press (>500ms, no movement) → invokes `onLongPress(ayah, {x, y})`.
 *
 * Returns handlers to spread onto the ayah element:
 *   onPointerDown, onPointerMove, onPointerUp, onPointerLeave.
 *
 * Movement > ~10px cancels the pending long-press (so scrolling never
 * triggers the action menu on touch devices).
 */
export default function useAyahLongPress({ onTap, onLongPress, holdMs = 500 }) {
  const pressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);
  const originRef = useRef(null);

  const start = (ayah, e) => {
    longPressFiredRef.current = false;
    const x = e?.clientX ?? e?.touches?.[0]?.clientX ?? null;
    const y = e?.clientY ?? e?.touches?.[0]?.clientY ?? null;
    originRef.current = x != null && y != null ? { x, y } : null;
    pressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      onLongPress?.(ayah, {
        x: x ?? window.innerWidth / 2,
        y: y ?? window.innerHeight / 2,
      });
    }, holdMs);
  };

  const cancel = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    originRef.current = null;
  };

  const move = (e) => {
    if (!pressTimerRef.current || !originRef.current) return;
    const cx = e?.clientX ?? e?.touches?.[0]?.clientX ?? null;
    const cy = e?.clientY ?? e?.touches?.[0]?.clientY ?? null;
    if (cx == null || cy == null) return;
    const dx = cx - originRef.current.x;
    const dy = cy - originRef.current.y;
    if (dx * dx + dy * dy > 100) cancel();
  };

  const tap = (ayah) => {
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    onTap?.(ayah);
  };

  return { start, cancel, move, tap };
}
